import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";

// ==== GENERATOR ====
//
// Zadanie: oblicz a_n w ciągu geometrycznym
// a_n = a_1 * q^{n-1}

export function generateTask(options = {}) {
  if (options.simple) {
    const a1Options = [-5, -4, -3, -2, 1, 2, 3, 4, 5];
    const qOptions = [-2, 2, 3];
    const a1 = a1Options[Math.floor(Math.random() * a1Options.length)];
    const q = qOptions[Math.floor(Math.random() * qOptions.length)];
    const n = Math.floor(Math.random() * 3) + 3;
    return {
      type: "FindNthTermGeoTask",
      data: { a1, q, n, a_n: a1 * Math.pow(q, n - 1) },
    };
  }

  // a1 z zakresu, różne od zera
  let a1;
  do {
    a1 = Math.floor(Math.random() * 41) - 20; // -20..20
  } while (a1 === 0);

  // ====== definicja dopuszczalnych przypadków (q,n) ======
  // min n = 3 (czyli wykładnik >= 2)
  const minN = 3;

  // max n zależnie od |q|
  const maxNByAbsQ = {
    2: 11, // potęga 10
    3: 6,  // potęga 5
    4: 5,  // potęga 4
    5: 5,  // potęga 4
    6: 4,  // potęga 3
    7: 4,  // potęga 3
  };

  // wagi (żeby 2 i 3 wypadały częściej)
  // Każdy "przypadek" (q,n) ma wagę zależną tylko od |q|.
  const weightByAbsQ = {
    2: 3,
    3: 2,
    4: 1,
    5: 1,
    6: 1,
    7: 1,
  };

  // Budujemy listę przypadków (q,n) + ich wagi
  const cases = [];
  for (const absQ of [2, 3, 4, 5, 6, 7]) {
    const maxN = maxNByAbsQ[absQ];
    for (const n of range(minN, maxN)) {
      // q dodatnie i ujemne jako osobne przypadki (równa szansa znaku)
      cases.push({ q: absQ, n, w: weightByAbsQ[absQ] });
      cases.push({ q: -absQ, n, w: weightByAbsQ[absQ] });
    }
  }

  const picked = weightedPick(cases);
  const q = picked.q;
  const n = picked.n;

  const exponent = n - 1;
  const a_n = a1 * Math.pow(q, exponent);

  return {
    type: "FindNthTermGeoTask",
    data: { a1, q, n, a_n },
  };
}

// ===== helpers =====
function range(a, b) {
  const out = [];
  for (let i = a; i <= b; i++) out.push(i);
  return out;
}

function weightedPick(items) {
  const total = items.reduce((s, it) => s + it.w, 0);
  let r = Math.random() * total;
  for (const it of items) {
    r -= it.w;
    if (r <= 0) return it;
  }
  return items[items.length - 1];
}


// ==== RENDER ====

export function renderTask({ data }) {
  const { a1, q, n } = data;

  return (
    <>
      <p>
        Dany jest ciąg geometryczny, w którym{" "}
        <InlineMath math={`a_1 = ${a1}`} /> oraz{" "}
        <InlineMath math={`q = ${q}`} />.
      </p>
      <p>
        Oblicz wyraz <InlineMath math={`a_{${n}}`} /> tego ciągu.
      </p>
    </>
  );
}

export function renderInput(_, userAnswer, onChange, disabled) {
  return (
    <div className="flex items-center gap-2">
      <InlineMath math="a_n =" />
      <FractionInput
        value={userAnswer}
        onChange={onChange}
        disabled={disabled}
        label="a_n"
      />
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object") return false;

  const given = userAnswer.parsed;
  if (given === null || isNaN(given)) return false;

  return Math.abs(given - data.a_n) < 0.01;
}

export function renderAnswer({ data }) {
  const { n, a_n } = data;
  return <InlineMath math={`a_{${n}} = ${a_n}`} />;
}

export function renderExplanation({ data }) {
  const { a1, q, n, a_n } = data;

  const exponent = n - 1;
  const power = Math.pow(q, exponent);

  return (
    <div className="space-y-2">
      <p>
        <strong>Krok 1:</strong> Korzystamy ze wzoru ogólnego ciągu
        geometrycznego:
      </p>
      <BlockMath math={`a_n = a_1 \\cdot q^{n-1}`} />

      <p>
        <strong>Krok 2:</strong> Podstawiamy dane:
      </p>
      <BlockMath math={`a_{${n}} = ${a1} \\cdot ${q}^{${exponent}}`} />
      <BlockMath math={`a_{${n}} = ${a1} \\cdot ${power}`} />

      <p>
        <strong>Krok 3:</strong> Obliczamy wartość:
      </p>
      <BlockMath math={`a_{${n}} = ${a_n}`} />
    </div>
  );
}

export function renderUserAnswer(userAnswer, task) {
  const n = task?.data?.n;

  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw) {
    return <i>(brak odpowiedzi)</i>;
  }

  return <InlineMath math={`a_{${n}} = ${userAnswer.raw}`} />;
}
